import React from 'react';
import { Tag, Typography } from '@navi/web-ui/lib/primitives';
import { NaviTableColumnDef } from '@cp/src/components/NaviTable/interfaces';
import { DateFormat } from '@cp/src/utils/DateHelper';
import dayjs from 'dayjs';
import { getAnomalyActionUrl } from '../../utils';
import { copyToClipboard } from '@cp/src/utils/commonUtils';
import { UserActivityStatusMap, AnomalyPriorityColor, IAnomalyData } from '../../types';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import DaysTillEscalation from '../../components/DaysTillEscalation';
import Actions from '../../components/TableCells/Actions';
import RaisedFor from '../../components/TableCells/RaisedFor';

export const copyAnomalyUrl = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  anomalyReferenceId: string
) => {
  e?.stopPropagation();
  const url = getAnomalyActionUrl(anomalyReferenceId);
  const { origin, pathname } = window.location;
  const updatedUrl = `${origin}${pathname}${url}`;
  copyToClipboard(updatedUrl)
    .then(() => {
      toast.success('Anomaly link copied');
    })
    .catch(() => {
      toast.error('Error copying to clipboard');
    });
};

export const getOpenAnomalyColumnDefs = (
  viewAnomalyHandler: (anomalyData: IAnomalyData) => void
): NaviTableColumnDef<any, any>[] => {
  return [
    {
      accessorKey: 'anomalyId',
      header: 'Anomaly ID',
      size: 110
    },
    {
      accessorKey: 'createdAt',
      header: 'Created Date',
      size: 120,
      enableSorting: true,
      cell: info => {
        const value = info.row.original?.createdAt;
        return dayjs(value)?.format(DateFormat.LONG_DATE_FORMAT_WITHOUT_TIME);
      }
    },
    {
      accessorKey: 'subtype',
      header: 'Type',
      enableSorting: true,
      size: 260,
      cell: info => {
        const { type, subtype } = info.row.original || {};
        return (
          <div className="w-full">
            <Typography color="var(--grayscale-2)" variant="h4" className="truncate">
              {subtype}
            </Typography>
            <Typography color="var(--grayscale-3)" className="truncate mt-1" variant="h4">
              ({type})
            </Typography>
          </div>
        );
      },
      tooltip: info => {
        const { type, subtype } = info.row.original || {};
        return {
          content: (
            <div>
              <Typography variant="p4">Anomaly Type: {subtype}</Typography>
              <Typography className="mt-1" variant="p4">
                Anomaly Description: {type}
              </Typography>
            </div>
          )
        };
      }
    },
    {
      accessorKey: 'raisedFor',
      header: 'Raised For',
      size: 200,
      cell: info => <RaisedFor info={info?.row?.original} />
    },
    {
      accessorKey: 'agency',
      header: 'Agency',
      size: 200,
      tooltip: true,
      cell: info => {
        const { raisedFor } = info?.row?.original || {};
        return (
          <Typography color="var(--grayscale-2)" variant="h4" className="truncate">
            {raisedFor?.agencyName}
          </Typography>
        );
      }
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      enableSorting: true,
      size: 110,
      cell: info => {
        const priority: string = info.row.original.priority;
        const color = AnomalyPriorityColor[priority as keyof typeof AnomalyPriorityColor];
        if (!priority || !color) return null;
        return <Tag color={color} variant="transparent" label={priority} />;
      }
    },
    {
      accessorKey: 'nextEscalationAt',
      header: 'Days Till Escalation',
      enableSorting: true,
      size: 120,
      cell: info => {
        const { daysRemaining, escalationPercentage } = info.row.original || {};
        return (
          <div className="relative w-10 h-10">
            <DaysTillEscalation
              daysTillEscalation={daysRemaining}
              progressPercent={escalationPercentage}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'estimatedTime',
      header: 'Activity',
      size: 120,
      cell: info => {
        const { rca, estimatedTime } = info.row.original || {};
        if (!estimatedTime && !rca) return '-';
        const formattedDate = estimatedTime?.at
          ? dayjs(estimatedTime?.at).format(DateFormat.DD_MMM)
          : null;
        return (
          <div className="flex flex-col gap-2">
            {rca ? <Tag color={'green'} variant="transparent" label={'RCA Filled'} /> : null}
            {formattedDate && estimatedTime?.status ? (
              <Tag
                color={UserActivityStatusMap[estimatedTime?.status]?.color}
                variant="transparent"
                label={`${'ETA'} (${formattedDate})`}
              />
            ) : null}
          </div>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 150,
      cell: info => <Actions viewAnomalyHandler={viewAnomalyHandler} info={info?.row?.original} />
    }
  ];
};
